import './modal.component.scss';

import type { FunctionComponent } from 'preact';
import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import {
  selectAccountSettings,
  selectAccountSettingsLoaded,
} from '../../../auth/store/auth.selectors';
import { loadAccountSettings } from '../../../auth/store/auth.thunk';
import { IconCloseCircle } from '../../../common/icons/icon-close-circle';
import { useTranslation } from '../../../i18n';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

interface ModalProps {
  onClose: () => void;
  children: any;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  isTopmost?: () => boolean;
  ariaLabel?: string;
  maxWidth?: number;
  /** Match the modal frame to the host block's styling (e.g. the video
   *  player's border). When absent, the account-level Colors & Borders
   *  settings apply, then the stylesheet defaults. The close button keeps
   *  its gray background (its icon is a fixed dark fill) but follows the
   *  effective radius on its outer corner. */
  borderRadius?: string;
  borderWidth?: string;
  borderColor?: string;
}

export const Modal: FunctionComponent<ModalProps> = ({
  onClose,
  children,
  closeOnBackdrop = false,
  closeOnEsc = true,
  isTopmost,
  ariaLabel,
  maxWidth,
  borderRadius,
  borderWidth,
  borderColor,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const accountSettingsLoaded = useAppSelector(selectAccountSettingsLoaded);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};

  useEffect(() => {
    if (!accountSettingsLoaded) {
      dispatch(loadAccountSettings());
    }
  }, [accountSettingsLoaded, dispatch]);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // ESC key handler
    const handleEsc = (e: KeyboardEvent) => {
      if (closeOnEsc && e.key === 'Escape' && (isTopmost?.() ?? true)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, closeOnEsc]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    contentRef.current?.focus();
    return () => {
      previouslyFocused?.focus?.();
    };
  }, []);

  const trapFocus = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = contentRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables?.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleBackdropClick = (e: MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };

  const settingsPx = (value: string | number | null | undefined): string | undefined =>
    value != null && `${value}` !== '' ? `${value}px` : undefined;

  const effectiveRadius = borderRadius ?? settingsPx(accountSettings?.borderRadius);
  const effectiveWidth = borderWidth ?? settingsPx(accountSettings?.borderWidth);
  const effectiveColor = borderColor ?? (accountSettings?.cardBorderColor || undefined);

  const contentStyle = {
    ...(maxWidth ? { maxWidth: `${maxWidth}px` } : {}),
    ...(effectiveRadius ? { borderRadius: effectiveRadius } : {}),
    ...(effectiveWidth ? { borderWidth: effectiveWidth } : {}),
    ...(effectiveColor ? { borderColor: effectiveColor } : {}),
  };

  const closeStyle = effectiveRadius
    ? { borderTopRightRadius: 0, borderBottomLeftRadius: effectiveRadius }
    : undefined;

  return (
    <div class="single-modal-backdrop" onClick={handleBackdropClick}>
      <div
        class="single-modal-content"
        style={contentStyle}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? t('common.dialog')}
        tabIndex={-1}
        ref={contentRef}
        onKeyDown={trapFocus}
      >
        <button
          class="single-modal-close-button"
          style={closeStyle}
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <IconCloseCircle width={18} height={18} fill="#000" />
        </button>
        {children}
      </div>
    </div>
  );
};
